import React, { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import { Row, Modal, ModalHeader, ModalBody, ModalFooter, Button, Alert, Card, CardBody } from 'reactstrap';
import Gantt from 'frappe-gantt';

// ---------- START CONSTANTS ----------
const RECURRENCES_DURATION_DATA = {
  daily: { days: 1, warnDays: 0 },
  weekly: { days: 7, warnDays: 2 },
  biweekly: { days: 14, warnDays: 5 },
  monthly: { days: 30, warnDays: 5 },
  quarterly: { days: 90, warnDays: 15 },
  semiannual: { days: 180, warnDays: 15 },
  annual: { days: 365, warnDays: 30 }
};
// ---------- END CONSTANTS ----------

// ---------- START UTILITY FUNCTIONS ----------
const addRecurrence = (date, recurrence) => {
  const dateMoment = moment(date).clone();

  switch (recurrence) {
    case 'daily': return dateMoment.add(1, 'day').toDate();
    case 'weekly': return dateMoment.add(1, 'week').toDate();
    case 'biweekly': return dateMoment.add(2, 'weeks').toDate();
    case 'monthly': return dateMoment.add(1, 'month').toDate();
    case 'quarterly': return dateMoment.add(3, 'months').toDate();
    case 'semiannual': return dateMoment.add(6, 'months').toDate();
    case 'annual': return dateMoment.add(1, 'year').toDate();
    default:
      console.error('Invalid recurrence', recurrence);
      return date;
  }
};

const parseDate = (dateString) => {
  if (!dateString) return null;
  try {
    const date = moment(dateString).toDate();
    return isNaN(date.getTime()) ? null : date;
  } catch (error) {
    console.error('Error parsing date', { error, dateString });
    return null;
  }
};

const startOfDay = (date) => {
  return moment(date).startOf('day').toDate();
};

const clamp = (value) => Math.max(0, Math.min(100, value));
// ---------- END UTILITY FUNCTIONS ----------

const ComplianceActivityRecurrencePreviewModal = (props) => {
  const { activity } = props;

  const [isOpen, setIsOpen] = useState(false);

  const ganttContainerRef = useRef(null);
  const ganttInstanceRef = useRef(null);

  let name = activity?.name;
  let start_date = activity?.start_date || moment().format('YYYY-MM-DD');
  let deadline = activity?.deadline || moment().add(1, 'month').format('YYYY-MM-DD');
  let recurrence = activity?.recurrence;


  // ---------- START RECURRENCE DATA ----------
  const getRecurrenceData = useCallback(() => {
    const days = RECURRENCES_DURATION_DATA[recurrence]?.days || 0;
    const warnDays = RECURRENCES_DURATION_DATA[recurrence]?.warnDays || 0;
    const thresholdDays = days - warnDays;
    return { days, warnDays, thresholdDays };
  }, [recurrence]);

  const recurrenceData = useMemo(() => getRecurrenceData(), [getRecurrenceData]);
  // ---------- END RECURRENCE DATA ----------

  // ---------- START BUILD TASKS ----------
  const buildTasks = useMemo(() => {
    if (!start_date || !deadline) return [];

    const startDate = parseDate(start_date);
    const deadlineDate = parseDate(deadline);

    if (!startDate || !deadlineDate) return [];

    const tasks = [];
    const todayFull = startOfDay(moment().toDate());
    const endIncluded = addRecurrence(addRecurrence(deadlineDate, 'daily'), 'daily');

    tasks.push({
      id: 'current_activity',
      name: name || 'Actividad',
      start: moment(todayFull).format('YYYY-MM-DD'),
      end: moment(endIncluded).format('YYYY-MM-DD'),
      progress: 100,
      custom_class: 'current-activity'
    });

    if (recurrence && recurrence !== 'spot') {
      const { thresholdDays } = recurrenceData;
      const nextCreationDate = moment(startDate).add(thresholdDays, 'days').toDate();
      const nextDeadline = addRecurrence(deadlineDate, recurrence);
      const nextEndIncluded = addRecurrence(addRecurrence(nextDeadline, 'daily'), 'daily');

      tasks.push({
        id: 'next_activity',
        name: 'Sig. Actividad',
        start: moment(nextCreationDate).format('YYYY-MM-DD'),
        end: moment(nextEndIncluded).format('YYYY-MM-DD'),
        progress: 100,
        dependencies: 'current_activity',
        custom_class: 'next-activity'
      });
    }

    return tasks;
  }, [name, start_date, deadline, recurrence, recurrenceData]);
  // ---------- END BUILD TASKS ----------

  // ---------- START APPLY SEGMENTED PROGRESS ----------
  const applySegmentedProgress = useCallback((activityType) => {
    if (!start_date || !deadline) return;

    const startDate = parseDate(start_date);
    const deadlineDate = parseDate(deadline);

    if (!startDate || !deadlineDate) return;

    let barStart, p1Date, p2Date, endDate;

    if (activityType === 'current-activity') {
      barStart = startOfDay(moment().toDate());
      p1Date = startOfDay(startDate);
      p2Date = startOfDay(addRecurrence(deadlineDate, 'daily'));
      endDate = startOfDay(addRecurrence(addRecurrence(deadlineDate, 'daily'), 'daily'));
    } else if (activityType === 'next-activity') {
      if (!recurrence || recurrence === 'spot') return;

      const { thresholdDays } = recurrenceData;
      const nextCreationDate = startOfDay(moment(startDate).add(thresholdDays, 'days').toDate());
      const nextStartDate = startOfDay(addRecurrence(startDate, recurrence));
      const nextDeadline = startOfDay(addRecurrence(deadlineDate, recurrence));

      barStart = nextCreationDate;
      p1Date = nextStartDate;
      p2Date = startOfDay(addRecurrence(nextDeadline, 'daily'));
      endDate = startOfDay(addRecurrence(addRecurrence(nextDeadline, 'daily'), 'daily'));
    } else {
      return;
    }

    const total = endDate - barStart;
    if (total <= 0) return;

    const p1 = clamp(((p1Date - barStart) / total) * 100);
    const p2 = clamp(((p2Date - barStart) / total) * 100);

    if (!ganttContainerRef.current) return;
    const svg = ganttContainerRef.current.querySelector('svg');
    if (!svg) return;

    const svgns = 'http://www.w3.org/2000/svg';
    let defs = svg.querySelector('defs');
    if (!defs) {
      defs = document.createElementNS(svgns, 'defs');
      svg.insertBefore(defs, svg.firstChild);
    }

    const gradId = `grad-${activityType}`;
    const prev = svg.querySelector(`#${gradId}`);
    if (prev) prev.remove();

    const grad = document.createElementNS(svgns, 'linearGradient');
    grad.setAttribute('id', gradId);
    grad.setAttribute('x1', '0%');
    grad.setAttribute('y1', '0%');
    grad.setAttribute('x2', '100%');
    grad.setAttribute('y2', '0%');

    const stops = [
      { offset: 0, color: '#fe7e13' },
      { offset: p1, color: '#fe7e13' },
      { offset: p1, color: '#FFC107' },
      { offset: p2, color: '#FFC107' },
      { offset: p2, color: '#dd3a4a' },
      { offset: 100, color: '#dd3a4a' }
    ];

    stops.forEach(({ offset, color }) => {
      const stop = document.createElementNS(svgns, 'stop');
      stop.setAttribute('offset', `${offset}%`);
      stop.setAttribute('stop-color', color);
      grad.appendChild(stop);
    });

    defs.appendChild(grad);

    const progress = ganttContainerRef.current?.querySelector(`.bar-wrapper.${activityType} .bar-progress`) ||
      ganttContainerRef.current?.querySelector(`.bar-progress.${activityType}`);

    if (progress) {
      progress.style.fill = `url(#${gradId})`;
      progress.style.stroke = 'none';
      progress.style.shapeRendering = 'crispEdges';
    }
  }, [start_date, deadline, recurrence, recurrenceData]);
  // ---------- END APPLY SEGMENTED PROGRESS ----------

  // ---------- START EXPLAIN GANTT ----------
  const renderNextActivityCard = useMemo(() => {
    if (!recurrence || recurrence === 'spot') return null;

    const startDate = parseDate(start_date);
    const deadlineDate = parseDate(deadline);
    if (!startDate || !deadlineDate) return null;

    const { days, warnDays, thresholdDays } = recurrenceData;
    const nextStartDate = addRecurrence(startDate, recurrence);
    const nextDeadline = addRecurrence(deadlineDate, recurrence);
    const nextCreationDate = moment(startDate).add(thresholdDays, 'days').toDate();
    const overdueStart = moment(nextDeadline).add(1, 'day').toDate();

    return (
      <Card className="mt-3" key="next-activity">
        <CardBody>
          <h5 className="text-info mb-0">
            La Siguiente se iniciará a partir del
            <strong> {moment(nextStartDate).format('DD/MM/YYYY')}</strong> con una recurrencia
            <strong> {I18n.t(`activerecord.attributes.compliance/activity.recurrence.${recurrence}`)}</strong>.
          </h5>
          <div className="small text-muted mt-2">
            <ul className="mb-0 pl-3">
              <li>Ciclo: <strong>{days}</strong> días ({I18n.t(`activerecord.attributes.compliance/activity.recurrence.${recurrence}`)})</li>
              <li>Fecha de inicio: <strong>{moment(nextStartDate).format('DD/MM/YYYY')}</strong></li>
              <li>Dias previos de planificación: <strong>{warnDays}</strong> días <strong>(planificación de la actividad)</strong></li>
              <li>Fecha de creación: <strong>{moment(nextCreationDate).format('DD/MM/YYYY')}</strong> (Se toma la fecha de inicio - los dias previos de planificación <strong>({warnDays})</strong>)</li>
              <li>Fecha de cumplimiento: <strong>{moment(nextDeadline).format('DD/MM/YYYY')}</strong></li>
              <li>Si a la fecha <strong>{moment(overdueStart).format('DD/MM/YYYY')}</strong> no se ha completado, se considera atrasada</li>
            </ul>
          </div>
        </CardBody>
      </Card>
    );
  }, [start_date, deadline, recurrence, recurrenceData]);

  const renderExplainGantt = () => {
    const startDate = parseDate(start_date);
    const deadlineDate = parseDate(deadline);

    if (!startDate || !deadlineDate) return null;

    const { days } = recurrenceData;

    return (
      <div className="mt-3">
        <Card>
          <CardBody>
            <h5 className="text-info mb-0">
              Esta actividad se iniciará a partir del
              <strong> {moment(startDate).format('DD/MM/YYYY')}</strong> con una recurrencia
              <strong> {I18n.t(`activerecord.attributes.compliance/activity.recurrence.${recurrence}`)}</strong>.
            </h5>
            <div className="small text-muted mt-2">
              <ul className="mb-0 pl-3">
                <li>Ciclo: <strong>{days}</strong> días ({I18n.t(`activerecord.attributes.compliance/activity.recurrence.${recurrence}`)})</li>
                <li>Fecha de creación: <strong>{moment().format('DD/MM/YYYY')}</strong></li>
                <li>Fecha de inicio: <strong>{moment(startDate).format('DD/MM/YYYY')}</strong></li>
                <li>Dias previos de planificación: <strong>{moment(startDate).diff(moment(), 'days') || 0}</strong> días <strong>(planificación de la actividad)</strong></li>
                <li>Fecha de cumplimiento: <strong>{moment(deadlineDate).format('DD/MM/YYYY')}</strong></li>
                <li>Si a la fecha <strong>{moment(addRecurrence(deadlineDate, 'daily')).format('DD/MM/YYYY')}</strong> no se ha completado, se considera atrasada</li>
              </ul>
            </div>
          </CardBody>
        </Card>
        {renderNextActivityCard}
      </div>
    );
  };
  // ---------- END EXPLAIN GANTT ----------

  // ---------- START LEGEND ACTIVITY STATE ----------
  const renderLegendActivityState = () => {
    return (
      <Card className="mt-3">
        <CardBody>
          <div className="d-flex align-items-center mb-1">
            <span className="badge badge-soft-warning mr-2" style={{ color: '#fe7e13', border: '1px solid #fe7e13', background: 'rgb(255, 243, 234)' }}>
              Planificada
            </span>
            <small>La actividad estara en estado planificada hasta un dia antes de la fecha de inicio <strong>(No estará disponible para ser completada)</strong></small>
          </div>
          <div className="d-flex align-items-center mb-1">
            <span className="badge badge-soft-warning mr-2">Pendiente</span>
            <small>La actividad estara en estado pendiente desde la fecha de inicio <strong>(Estará disponible para ser completada)</strong></small>
          </div>
          <div className="d-flex align-items-center mb-1">
            <span className="badge badge-soft-danger mr-2">Atrasada</span>
            <small>La actividad estara en estado atrasada si no se ha completado <strong>(Después de la fecha de cumplimiento)</strong></small>
          </div>
        </CardBody>
      </Card>
    );
  };
  // ---------- END LEGEND ACTIVITY STATE ----------

  // ---------- START INITIALIZE GANTT ----------
  const initializeGantt = useCallback(() => {
    if (!ganttContainerRef.current) {
      console.warn('Gantt container ref not available');
      return;
    }

    ganttContainerRef.current.innerHTML = '';

    if (buildTasks.length === 0) {
      return;
    }

    try {
      const ganttInstance = new Gantt(ganttContainerRef.current, buildTasks, {
        view_mode: 'Day',
        language: 'es',
        custom_popup_html: null,
        container_height: '350'
      });

      ganttInstanceRef.current = ganttInstance;

      setTimeout(() => {
        if (ganttContainerRef.current && ganttInstanceRef.current) {
          applySegmentedProgress('current-activity');
          applySegmentedProgress('next-activity');
        }
      }, 200);

      const handleWheel = (e) => {
        if (!ganttContainerRef.current) return;
        if (e.deltaY !== 0 && !e.shiftKey) {
          e.preventDefault();
          ganttContainerRef.current.scrollLeft += e.deltaY;
        }
        if (ganttContainerRef.current && ganttInstanceRef.current) {
          applySegmentedProgress('current-activity');
          applySegmentedProgress('next-activity');
        }
      };

      if (ganttContainerRef.current) {
        ganttContainerRef.current.addEventListener('wheel', handleWheel, { passive: false });
        ganttContainerRef.current._wheelHandler = handleWheel;
      }
    } catch (error) {
      console.error('Error creating Gantt instance:', error);
    }
  }, [buildTasks, applySegmentedProgress, start_date, deadline, recurrence]);
  // ---------- END INITIALIZE GANTT ----------

  // ---------- START CLEANUP EFFECT ----------
  useEffect(() => {
    if (!isOpen) {
      if (ganttContainerRef.current && ganttContainerRef.current._wheelHandler) {
        ganttContainerRef.current.removeEventListener('wheel', ganttContainerRef.current._wheelHandler);
        delete ganttContainerRef.current._wheelHandler;
      }
      if (ganttContainerRef.current) {
        ganttContainerRef.current.innerHTML = '';
      }
      if (ganttInstanceRef.current) {
        ganttInstanceRef.current = null;
      }
    }
  }, [isOpen]);
  // ---------- END CLEANUP EFFECT ----------

  // ---------- START HANDLERS ----------
  const handleModalOpened = useCallback(() => {
    setTimeout(() => {
      initializeGantt();
    }, 100);
  }, [initializeGantt]);

  const handleToggleModal = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);
  // ---------- END HANDLERS ----------

  // ---------- START RENDER FUNCTIONS ----------
  const renderWarningAlert = () => {
    if (!start_date && !deadline && !recurrence) {
      return (
        <Alert color="warning" className="mb-0">
          Seleccione una fecha de inicio, una fecha de cumplimiento y una recurrencia para obtener la previsualización
        </Alert>
      );
    }
    return null;
  };

  const renderGanttContainer = () => {
    return (
      <div
        ref={ganttContainerRef}
        id="gantt-embed"
        style={{
          overflowY: 'hidden',
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          minHeight: '350px'
        }}
      />
    );
  };

  const renderModal = () => {
    return (
      <Modal
        isOpen={isOpen}
        toggle={handleToggleModal}
        size="xl"
        unmountOnClose={false}
        onOpened={handleModalOpened}
      >
        <ModalHeader toggle={handleToggleModal}>
          Recurrencia de la actividad
        </ModalHeader>
        <ModalBody>
          {renderWarningAlert()}
          {renderGanttContainer()}
          {renderLegendActivityState()}
          {renderExplainGantt()}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleToggleModal}>
            Cerrar
          </Button>
        </ModalFooter>
      </Modal>
    );
  };

  const renderPreviewButton = () => {
    return (
      <div className="text-start mt-3">
        <Button
          type="button"
          color="outline-primary"
          onClick={handleToggleModal}
        >
          {I18n.t('actions.preview')}
        </Button>
      </div>
    );
  };
  // ---------- END RENDER FUNCTIONS ----------

  return (
    <Row>
      {renderPreviewButton()}
      {renderModal()}
    </Row>
  );
};

export default ComplianceActivityRecurrencePreviewModal;
